/**
 * Phone KPIs (Prompt 2.12): `GET /analytics/phone` — calls, answered by AI,
 * containment, transfers, voicemail, abandoned, after-hours share, handle
 * time, ring → greeting / server response percentiles, per engine and per
 * number, daily series, latest harness mouth-to-ear per engine.
 */
import { get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export interface PhoneCallCounts {
  received: number;
  answeredByAi: number;
  resolvedByAi: number;
  ticketCreated: number;
  transferred: number;
  takenOver: number;
  voicemail: number;
  abandoned: number;
  rejected: number;
  failed: number;
  /** Conversed, but the request was left open. */
  unresolved?: number;
  containmentRate: number;
  avgHandleSec: number;
}

export interface LatencyStat {
  p50Ms: number | null;
  p95Ms: number | null;
  samples: number;
}

export interface PhoneEngineRow extends PhoneCallCounts {
  engine: string;
  ringToGreeting: LatencyStat;
  response: LatencyStat;
  harness: {
    m2eP50Ms: number | null;
    m2eP95Ms: number | null;
    passed: boolean;
    runAt: string;
  } | null;
}

export interface PhoneNumberRow extends PhoneCallCounts {
  phoneNumberId: string | null;
  e164: string | null;
  label: string | null;
}

export interface PhoneAnalytics {
  range: { from: string | null; to: string | null };
  totals: PhoneCallCounts & { afterHours: number; afterHoursShare: number };
  latency: { ringToGreeting: LatencyStat; response: LatencyStat };
  byEngine: PhoneEngineRow[];
  byNumber: PhoneNumberRow[];
  daily: Array<{ date: string; received: number; answeredByAi: number }>;
  /** 3.2/3.5: live-transfer funnel (absent on older APIs). */
  transfers?: TransferFunnel;
}

export interface TransferFunnel {
  attempted: number;
  answered: number;
  failed: number;
  backToAi: number;
  answerRate: number;
  byLabel: Array<{ label: string; attempted: number; answered: number; failed: number }>;
}

export interface PhoneAnalyticsQuery {
  agentId?: string | null;
  phoneNumberId?: string | null;
  engine?: string | null;
  from?: string | null;
  to?: string | null;
}

export const phoneAnalyticsService = {
  get(q: PhoneAnalyticsQuery = {}): Promise<PhoneAnalytics> {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(q)) if (v) params[k] = v;
    return get<PhoneAnalytics>(endpoints.analytics.phone, { params });
  },
} as const;
